import * as dompurify from "dompurify";
import _ = require("lodash");
import hash = require("object-hash");
import * as React from "react";
import { CellIndexMapping, ColumnType } from "./UIConstants";
import { checkExcelError } from "../helpers/common/DataUtilityHelper";
import { DataAddInStateManager } from "../helpers/common/DataAddInStateManager";
import { resources } from "../resources/resources";

class Utility extends Object {
  /**
   * Creates a rich text element to show rich text. Eg. (Bold, Italic, Underline...)
   * rastwe: TODO: need some justification for having this here. Kendo editor?
   * @param {string} text - the text to display
   * @returns {Object} a rich text element to display
   * */
  static createRichTextElement(text, styleName, onClick) {
    /*why are we doing this? The text was created with Kendo which handles sanitizing. If you are showing
    text that was not created with a kendo editor in rich text view, I dont know why you would, but use the below
    function which seems useless as well. This will be removed soon.*/

    /*const sanitizer = dompurify.sanitize;
    text = sanitizer(_.unescape(text));*/
    return (
      <span
        onClick={(e) => {
          onClick && onClick(e);
        }}
        className={styleName ?? ""}
        dangerouslySetInnerHTML={Utility.createMarkup(text)}
      />
    );
  }

  static createRichTextElementName(text, styleName?: string) {
    /*why are we doing this? The text was created with Kendo which handles sanitizing. If you are showing
		text that was not created with a kendo editor in rich text view, I dont know why you would, but use the below
		function which seems useless as well. This will be removed soon.*/

    /*const sanitizer = dompurify.sanitize;
		text = sanitizer(_.unescape(text));*/
    return <span className={styleName ?? ""} dangerouslySetInnerHTML={Utility.createMarkup(text)} />;
  }

  /**
   * Private function to return html required for setting dangerous inner html
   * @param {string} text - the text to display
   * @returns {Object} an object with html set to the text passed in
   * */
  static createMarkup(text) {
    const sanitizedtext = Utility.getSanitizedText(text, { encode: false });
    return { __html: sanitizedtext };
  }

  /**
   * Get a sanitized text after parsing input for XSS.
   * @param {string} text - the input text
   * @returns {string} sanitized text
   * */
  static getSanitizedText(text, { encode = true }, sanitizeOnUnEscape = true) {
    const sanitizer = dompurify.sanitize;
    let sanitizedText = "";
    if (sanitizeOnUnEscape) {
      const unescapeText = _.unescape(text);
      sanitizedText = sanitizer(unescapeText);
    } else {
      sanitizedText = _.unescape(sanitizer(text));
    }
    if (encode) {
      sanitizedText = encodeURIComponent(sanitizedText);
    }
    return sanitizedText;
  }

  static getInnerTextFromRichText(html) {
    let element = document.createElement("div");
    element.innerHTML = html;
    // fix special chart from Kendo: &#65279
    return element.innerText.replace(/\uFEFF/g, "").trim();
  }

  static getEndingDataExcelCellName = (columns) => {
    let excelCellName = null;
    let endColumn = _.sortBy(columns, ["CellDisplayIndex"]).reverse()[0];
    if (endColumn != null) {
      excelCellName = CellIndexMapping[endColumn.CellDisplayIndex];
    }

    return excelCellName;
  };

  static getStartingDataExcelCellName = (columns) => {
    let excelCellName = null;
    let endColumn = _.sortBy(columns, ["CellDisplayIndex"])[0];
    if (endColumn != null) {
      excelCellName = CellIndexMapping[endColumn.CellDisplayIndex];
    }

    return excelCellName;
  };

  static findNumericCharacters(input: string): string[] {
    // Define a regular expression to match numeric characters
    const regex = /\d/g;

    // Use the match method to find all numeric characters
    const matches = input.match(regex);

    // If matches are found, return them; otherwise, return an empty array
    return matches ? matches : [];
  }

  static findNumericData = (input: string): string => {
    let numericCharactersString = "";
    for (const char of input) {
      if (/\d/.test(char)) {
        numericCharactersString = numericCharactersString + char;
      }
    }
    return numericCharactersString;
  };

  static findNonNumericData = (input: string): string => {
    let numericCharactersString = "";
    for (const char of input) {
      if (!/\d/.test(char)) {
        numericCharactersString = numericCharactersString + char;
      }
    }
    return numericCharactersString;
  };

  static isNullOrUndefined = (value: any): any => {
    return value === null || value === undefined;
  };

  static isInvalidValue = (value: any, dataType: any = undefined): boolean => {
    return value === null || value === undefined || value === "undefined" || value === "" || value === "null";
  };

  static convertNewLinesToParagraphs(text: string): string {
    if (!text || checkExcelError(text)) return text;
    // Canvas colloboartion does not support new lines, so we need to convert new lines to paragraphs so that it visible as new line in Hercules
    let convertedText = text
      .split("\n")
      .map((line) => `<p>${line}</p>`)
      .join("");

    let sanatizeText = Utility.getSanitizedText(convertedText, { encode: false });

    return sanatizeText;
  }

  static getFormatedDescriptionToExcelFormat = (data: string): any => {
    if (data) {
      data = data.replace(/<p>/g, "");
      let description = data.replace(/<\/p>/g, "\n");
      description = Utility.getInnerTextFromRichText(description);
      // return "T1\nT3\nT45\nTEAEEE"
      return description;
    }

    return "";
  };

  static convertToBoolean = (value: string): any => {
    return value?.toLowerCase() == "true" ? true : false;
  };

  static getHashValueColumnsToUpdate = (configColumns, inputColumns): any => {
    const hashColumn = configColumns.find((column) => column.CellId == ColumnType.HashValue);
    if (hashColumn) {
      let hashCellIds = hashColumn.HashAttribue.HashCalculatedDataattributesName.map((hashData) => hashData.CellId);
      hashCellIds = [...hashCellIds, ...inputColumns];
      let dataToUpdateColumns = configColumns.filter((column) => hashCellIds.includes(column.CellId));
      return dataToUpdateColumns;
    }

    return [];
  };

  static getHashValueForRow = (configColumns, inputColumns, inputData, referenceData = null): any => {
    const hashColumn = configColumns.find((column) => column.CellId == ColumnType.HashValue);
    let hashValue: string = "";
    if (hashColumn) {
      let hashCellIds = hashColumn.HashAttribue.HashCalculatedDataattributesName.map((hashData) => hashData.CellId);
      hashCellIds = [...hashCellIds, ...inputColumns];
      let dataToUpdateColumns = configColumns.filter((column) => hashCellIds.includes(column.CellId));
      let dataValue = "";

      dataToUpdateColumns.forEach((column) => {
        let cellData = column.getCellData(inputData, [], [], "", referenceData);
        if(cellData) {
        dataValue = dataValue + cellData;
        }
      });

      hashValue = hash(dataValue);
    }

    return hashValue;
  };

  static getLanguageFromSettings = async (configuration) => {
    let currentResource = "";
    let serverSyncSettings = null;

    try {
      serverSyncSettings = await DataAddInStateManager.getPersistedServerSyncInfo(
        configuration.settings.sheetId,
        configuration.settings.sheetName
      );
    } catch (error) {
      console.log("Error in getting server sync settings", error);
    }

    const userCurrentLanguage = configuration?.settings.userLanguage;

    if (serverSyncSettings?.userLanguage && userCurrentLanguage != serverSyncSettings?.userLanguage) {
      currentResource = resources(serverSyncSettings?.userLanguage);
    }

    return currentResource;
  };

  static getReferenceData = async (column, configuration, userLanguage) => {
    if (column["referenceData"]?.getCellData) {
      let referenceInputData = column["referenceData"]?.getCellData(userLanguage);
      return referenceInputData;
    }

    return [];
  };

  static getLookupData = (referenceData, value, compareValueAttributeName="value"): any => {
    if(referenceData) {
    if (_.isArray(referenceData)) {
      let dataRow = referenceData.find((data) => data[compareValueAttributeName] == value);
      return dataRow;
    }

    return referenceData[value];
  }
  return "";
  };

  static updateEYCanvasDropDownValues = [
    {
      label: "Yes",
      value: "Yes",
    },
    {
      label: "No",
      value: "No",
    },
  ];

  static hasAnyProperty = (obj) => {
    return Object.keys(obj).length > 0;
  };
}
export default Utility;
